import {z} from 'zod'
import {PaginationResponse} from '@/types'

// Extra data schema for notifications
export const NotificationExtraData = z.object({
    patientName: z.string().optional(),
    projectCode: z.string().optional(),
    // The conversation's title on Conv-00x notifications — the BE sends it as `title`.
    title: z.string().optional(),
    // The conversation's id on Conv-00x notifications: enables opening the internal
    // conversations drawer when the notification has no link (patient-less conversation).
    // Optional forever: notifications persisted before the BE addition never carry it.
    conversationId: z.string().optional()
})

export type NotificationExtraData = z.infer<typeof NotificationExtraData>

/**
 * Catalogue of the notification types the backend is known to emit, kept for readability
 * and to type the handful of branch points the client actually has.
 *
 * This is deliberately NOT a validator: `Notification.type` is a plain `z.string()`. A closed
 * enum used to gate the response, so a single notification of a type the frontend did not
 * know (`Pri-008`, `Pri-009`, `Pri-010`, `Imp-Per-002` are all emitted in production) made the
 * whole notification list throw and fail to render. Keep this const out of the schemas — turning
 * it back into a `z.enum` reintroduces that outage at the next type the backend adds.
 */
export const NOTIFICATION_TYPE = {
    'Paz-001': 'Paz-001',
    'Paz-002': 'Paz-002',
    'Paz-003': 'Paz-003',
    'Paz-004': 'Paz-004',
    'Paz-005': 'Paz-005',
    'Paz-006': 'Paz-006',
    'RT-001': 'RT-001',
    'RT-002': 'RT-002',
    'Pri-001': 'Pri-001',
    'Pri-002': 'Pri-002',
    'Pri-003': 'Pri-003',
    'Pri-004': 'Pri-004',
    'Pri-005': 'Pri-005',
    'Pri-006': 'Pri-006',
    'Pri-007': 'Pri-007',
    'Pri-008': 'Pri-008',
    'Pri-009': 'Pri-009',
    'Pri-010': 'Pri-010',
    'Age-001': 'Age-001',
    'Age-001-Repeated': 'Age-001-Repeated',
    'Age-002': 'Age-002',
    'Age-003': 'Age-003',
    'Age-004': 'Age-004',
    'Age-005': 'Age-005',
    'Age-006': 'Age-006',
    'Age-007': 'Age-007',
    'Imp-Per-001': 'Imp-Per-001',
    'Imp-Per-002': 'Imp-Per-002',
    'Conv-001': 'Conv-001',
    'Conv-002': 'Conv-002'
} as const

export type KnownNotificationType = (typeof NOTIFICATION_TYPE)[keyof typeof NOTIFICATION_TYPE]

// Notification severity enum
export const NotificationSeverity = z.enum(['info', 'alert'])

export type NotificationSeverity = z.infer<typeof NotificationSeverity>

// The actual notification object from the API
export const Notification = z.object({
    id: z.string(),
    // Intentionally unvalidated — see NOTIFICATION_TYPE
    type: z.string(),
    title: z.string(),
    details: z.unknown().nullable(),
    bodyTemplate: z.string(),
    severity: NotificationSeverity,
    link: z.string().nullable(),
    triggeredBy: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    scheduledAt: z.coerce.date().nullable(),
    sentAt: z.coerce.date(),
    extraData: NotificationExtraData.nullable(),
    triggeredByUser: z.object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string()
    })
})

export type Notification = z.infer<typeof Notification>

// Socket notification schema aligned with the original Notification model
export const SocketNotification = z.object({
    id: z.string(),
    title: z.string(),
    bodyTemplate: z.string().optional(),
    body: z.string().optional(),
    // Intentionally unvalidated — see NOTIFICATION_TYPE
    type: z.string(),
    severity: NotificationSeverity,
    link: z.string().nullable(),
    triggeredBy: z.string().nullable(),
    createdAt: z.coerce.date(),
    sentAt: z.coerce.date().optional(),
    extraData: NotificationExtraData.nullish(),
    conversationId: z.string().nullish()
})

export type SocketNotification = z.infer<typeof SocketNotification>

// The UserNotification from the API matches this structure
export const UserNotification = z.object({
    id: z.string(),
    userId: z.string(),
    notificationId: z.string(),
    conversationId: z.string().nullable(),
    readAt: z.coerce.date().nullable(),
    details: z.unknown().nullable(),
    notification: Notification
})

export type UserNotification = z.infer<typeof UserNotification>

// Create a flattened version of UserNotification for internal use
export const NotificationsResponse = z.object({
    meta: PaginationResponse.extend({
        firstPage: z.number(),
        firstPageUrl: z.string(),
        lastPageUrl: z.string(),
        nextPageUrl: z.string().nullable(),
        previousPageUrl: z.string().nullable()
    }),
    data: z.array(UserNotification)
})

export type NotificationsResponse = z.infer<typeof NotificationsResponse>

export const NotificationCount = z.object({
    toRead: z.number(),
    total: z.number()
})

export type NotificationCount = z.infer<typeof NotificationCount>
